// src/components/resources/tools/generators/SocialMediaMockup/SocialMediaMockupGenerator.tsx

'use client'

import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent, type SetStateAction } from 'react'
import { toast } from 'sonner'
import {
  Download,
  Hash,
  Palette,
  RefreshCw,
  Upload,
  Music,
} from 'lucide-react'
import { clsx } from 'clsx'
import { sendResourceEvent, createEngagementTimers } from '~/lib/gtag'
import { captureAndDownload } from '../shared/exportImage'
import { ExportButton } from '../shared/ExportButton'
import { useExport } from '../shared/useExport'
import { PostExportPrompt } from '../shared/PostExportPrompt'
import { usePostExportPrompt } from '../shared/usePostExportPrompt'
import { GeneratorExportBridge } from '../shared/GeneratorExportBridge'
import {
  generatorHasExportHint,
  generatorHasPostExportFunnel,
} from '~/lib/resources/generator-growth-config'
import { waitForStable } from '../shared/waitForStable'
import { usePreviewDeferral, useStaleRef } from '../shared/usePreviewDeferral'
import { parseStoredSocialConfig } from './socialMediaConfigPersistence'
import {
  readPersistedGeneratorConfig,
  usePersistedGeneratorConfig,
} from '../shared/usePersistedGeneratorConfig'
import { SocialPreview } from './SocialPreview'
import {
  mergeSocialMediaConfig,
  mergeStoredSocialDraft,
  serializeSocialMediaDraft,
} from './socialMediaConfigUtils'
import type {
  EngagementMetrics,
  InteractionState,
  PlatformMeta,
  SocialMediaConfig,
  SocialMediaMockupGeneratorProps,
  SocialPlatform,
} from './types'

// Re-export the shared types and pure config helpers from their dedicated
// modules so existing importers of this entrypoint (the persistence module,
// the landing-variant registry, and the colocated tests) keep working after
// the decomposition.
export type {
  EngagementMetrics,
  InteractionState,
  SocialMediaConfig,
  SocialMediaMockupGeneratorProps,
  SocialPlatform,
} from './types'
export { mergeStoredSocialDraft, serializeSocialMediaDraft } from './socialMediaConfigUtils'

const ALL_METRICS: readonly (keyof EngagementMetrics)[] = ['likes', 'comments', 'shares']
const DEFAULT_METRIC_LABELS: Record<keyof EngagementMetrics, string> = {
  likes: 'Likes',
  comments: 'Comments',
  shares: 'Shares',
}

const PLATFORM_META: Record<SocialPlatform, PlatformMeta> = {
  instagram: { vertical: false, themeAware: false, visibleMetrics: ['likes', 'comments'], metricLabels: {}, captureWidthClass: 'max-w-md' },
  x: { vertical: false, themeAware: false, visibleMetrics: ALL_METRICS, metricLabels: { comments: 'Replies', shares: 'Reposts' }, captureWidthClass: 'max-w-xl' },
  linkedin: { vertical: false, themeAware: false, visibleMetrics: ALL_METRICS, metricLabels: {}, captureWidthClass: 'max-w-xl' },
  facebook: { vertical: false, themeAware: true, visibleMetrics: ALL_METRICS, metricLabels: { likes: 'Reactions' }, captureWidthClass: 'max-w-xl' },
  youtube: { vertical: false, themeAware: true, visibleMetrics: ['likes'], metricLabels: { likes: 'Views' }, captureWidthClass: 'max-w-md' },
  tiktok: { vertical: true, themeAware: false, visibleMetrics: ALL_METRICS, metricLabels: {}, captureWidthClass: 'max-w-[320px]' },
  'instagram-reels': { vertical: true, themeAware: false, visibleMetrics: ALL_METRICS, metricLabels: {}, captureWidthClass: 'max-w-[320px]' },
}

/**
 * Default configuration for the mockup generator.
 */
export const DEFAULT_CONFIG: SocialMediaConfig = {
  platform: 'instagram',
  username: 'AdSights Creative',
  handle: '@adsights',
  subtitle: 'Growth Marketing Agency',
  headline: 'Launch beautiful campaigns faster',
  content:
    'Design marketing assets that look native to every platform. Our creative intelligence suite helps you ship on-brand graphics in minutes, not hours.',
  hashtags: '#marketing #design #ads',
  background: 'linear-gradient(135deg, #101522, #1b2440)',
  showMetrics: true,
  metrics: {
    likes: 1280,
    comments: 342,
    shares: 178,
  },
  timestamp: '2h',
  verified: true,
  theme: 'dark',
  interactions: {
    liked: false,
    bookmarked: false,
    commented: false,
  },
  soundName: 'original sound - adsights',
  duration: '12:34',
}

const platformLabels: Record<SocialPlatform, string> = {
  instagram: 'Instagram',
  x: 'X (Twitter)',
  linkedin: 'LinkedIn',
  facebook: 'Facebook',
  youtube: 'YouTube',
  tiktok: 'TikTok',
  'instagram-reels': 'Instagram Reels',
}

const gradientPresets: string[] = [
  'linear-gradient(135deg, #667eea, #764ba2)',
  'linear-gradient(135deg, #00C6FF, #0072FF)',
  'linear-gradient(135deg, #f6d365, #fda085)',
  'linear-gradient(135deg, #434343, #000000)',
  'linear-gradient(135deg, #141E30, #243B55)',
  'linear-gradient(135deg, #ff9a9e, #fad0c4)',
]

/**
 * Main component for the Social Media Mockup Generator.
 */
export function SocialMediaMockupGenerator({ initialConfig, trackingSlug = 'social-media-post-mockup-generator' }: SocialMediaMockupGeneratorProps = {}): JSX.Element {
  const persistKey = trackingSlug
  const pinnedPlatform = initialConfig?.platform
  const baseConfig = useMemo(
    () => mergeSocialMediaConfig(DEFAULT_CONFIG, initialConfig),
    [initialConfig],
  )
  const [config, setConfigState] = useState<SocialMediaConfig>(baseConfig)
  const [restoreComplete, setRestoreComplete] = useState(false)
  const [isDirty, setIsDirty] = useState(false)

  const setConfig = useCallback((action: SetStateAction<SocialMediaConfig>): void => {
    setIsDirty(true)
    setConfigState(action)
  }, [])

  const serializeDraft = useCallback(
    (value: SocialMediaConfig): SocialMediaConfig =>
      serializeSocialMediaDraft(value, pinnedPlatform) as SocialMediaConfig,
    [pinnedPlatform],
  )

  const { clearPersisted } = usePersistedGeneratorConfig(
    persistKey,
    config,
    serializeDraft,
    { canPersist: restoreComplete && isDirty },
  )

  useEffect(() => {
    const parseDraft = (raw: unknown): Partial<SocialMediaConfig> | null =>
      parseStoredSocialConfig(raw, persistKey)

    const stored = readPersistedGeneratorConfig(persistKey, parseDraft)
    if (stored) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- post-mount localStorage restore; SSR must hydrate with defaults first
      setConfigState(mergeStoredSocialDraft(baseConfig, stored, pinnedPlatform))
      toast.message('Restored your last mockup draft', {
        description: 'Caption and layout were saved locally on this device. Re-upload images if needed.',
        action: {
          label: 'Clear',
          onClick: () => {
            clearPersisted()
            setIsDirty(false)
            setConfigState(baseConfig)
          },
        },
      })
    }
     
    setRestoreComplete(true)
  }, [baseConfig, clearPersisted, persistKey, pinnedPlatform])
  // Lower-priority snapshot of config that drives the heavy multi-platform
  // <SocialPreview> render. Controls (the aside) keep reading live `config`,
  // so caption/username/metric keystrokes commit synchronously while the
  // 7-platform preview re-renders at a lower priority — shortening the long
  // interaction task behind the ~392ms mobile INP on the generator pages.
  // Safe: the preview's only interactive elements are like/bookmark CLICKS
  // (no text inputs live in the preview), and html2canvas export waits for the
  // deferred snapshot to converge before reading the DOM (see handleExport), so
  // resting output and export match the live state.
  const { deferred: deferredConfig, isStale } = usePreviewDeferral(config)
  // Tracks whether the preview DOM is showing the latest (live) config. Read by
  // handleExport to avoid rasterizing a still-pending deferred frame. Committed
  // post-commit (from an effect) via useStaleRef so the ref reflects only
  // committed renders — never a deferred render React started but hasn't
  // committed; the export gate polls it across frames.
  const previewStaleRef = useStaleRef(isStale)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const previewRef = useRef<HTMLDivElement>(null)
  // Shared export lifecycle: warms html2canvas on mount, a synchronous
  // re-entrancy guard, and the `isExporting` flag for the Download affordance.
  const { isExporting, runExport } = useExport()
  const { open: postExportOpen, onOpenChange: onPostExportOpenChange, promptAfterExport } =
    usePostExportPrompt(trackingSlug)
  const showGrowthFunnel = generatorHasPostExportFunnel(trackingSlug)
  const showExportHint = generatorHasExportHint(trackingSlug)

  // Initialize engagement timers - memoized for stable references and optimal performance
  const [getTotalEngagementMs, getDeltaEngagementMs] = useMemo(
    () => createEngagementTimers(), 
    []
  )

  /**
   * Handle image upload for post media.
   */
  const handleImageUpload = useCallback((event: ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = e => {
      setConfig(prev => ({ ...prev, mediaUrl: e.target?.result as string }))
      
      // Track media image upload - user uploaded post content image
      sendResourceEvent('interact', {
        resourceType: 'tool',
        resourceSlug: trackingSlug,
        engagementMs: getDeltaEngagementMs(),
        params: {
          tool_type: 'generator',
          interaction_type: 'media_upload'
        }
      })
    }
    reader.readAsDataURL(file)
  }, [trackingSlug, getDeltaEngagementMs, setConfig])

  /**
   * Handle image upload for avatar.
   */
  const handleAvatarUpload = useCallback((event: ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = e => {
      setConfig(prev => ({ ...prev, avatarUrl: e.target?.result as string }))
      
      // Track avatar image upload - user uploaded profile avatar image
      sendResourceEvent('interact', {
        resourceType: 'tool',
        resourceSlug: trackingSlug,
        engagementMs: getDeltaEngagementMs(),
        params: {
          tool_type: 'generator',
          interaction_type: 'avatar_upload'
        }
      })
    }
    reader.readAsDataURL(file)
  }, [trackingSlug, getDeltaEngagementMs, setConfig])

  /**
   * Handle export of the mockup as PNG image.
   */
  const handleExport = useCallback((): Promise<void> => runExport(async beginCapture => {
    if (!previewRef.current) {
      toast.error('Preview not found. Please ensure the mockup is visible.')
      return
    }
    // The preview renders from `deferredConfig`; if the user edited copy or
    // switched platform and hit Download before that snapshot committed, wait
    // for it to catch up so the PNG can't ship the previous frame. Fail loudly
    // if it can't settle within the ceiling rather than export a stale frame.
    // We do this BEFORE `beginCapture` so the "Exporting…" label tracks the
    // actual capture, not this (up-to-600ms) gate — and never shows on the
    // abort path below.
    const stabilized = await waitForStable(() => !previewStaleRef.current)
    if (!stabilized) {
      toast.error('Preview is still updating — pause a moment, then export again.')
      return
    }
    // Capture is starting: show the disabled "Exporting…" state. captureAndDownload
    // yields a frame so this paints before html2canvas blocks the main thread —
    // ending the Download click's INP immediately instead of after the
    // multi-second rasterization.
    beginCapture()
    await captureAndDownload({
      element: previewRef.current,
      format: 'png',
      filename: `adsights-social-media-mockup-${config.platform}-${Date.now()}`,
      trackingSlug,
      engagementMs: getTotalEngagementMs(),
      onSuccess: () => {
        toast.success('Mockup exported successfully!')
        if (showGrowthFunnel) promptAfterExport()
      },
      onError: () => toast.error('Failed to export mockup. Please try again.'),
    })
  }), [runExport, config.platform, trackingSlug, getTotalEngagementMs, previewStaleRef, showGrowthFunnel, promptAfterExport])

  /**
   * Randomize the background gradient.
   */
  const randomizeGradient = useCallback((): void => {
    const next = gradientPresets[Math.floor(Math.random() * gradientPresets.length)]
    if (next) {
      setConfig(prev => ({ ...prev, background: next }))
    }
  }, [setConfig])

  /**
   * Handle user interaction (like, bookmark, comment).
   */
  const handleInteraction = useCallback((key: keyof InteractionState): void => {
    setConfig(prev => ({
      ...prev,
      interactions: {
        ...prev.interactions,
        [key]: !prev.interactions[key],
      },
    }))
  }, [setConfig])

  /**
   * Memoized label for the theme toggle button.
   */
  const themeLabel = useMemo(() => (config.theme === 'dark' ? 'Dark' : 'Light'), [config.theme])

  /**
   * The active platform's layout contract (metric labels, which metrics are
   * shown, theme-awareness, and capture width). `PLATFORM_META` is a const
   * map, so a direct lookup is a stable reference — no memoization needed.
   */
  const { metricLabels, visibleMetrics, themeAware: isThemeAware } =
    PLATFORM_META[config.platform]
  // The capture wrapper width must track the platform the PREVIEW is
  // actually rendering (deferred), not the live one — otherwise a platform
  // tab-switch would, for one frame, wrap the previous card in the new
  // platform's width and clip the 9:16 (TikTok/Reels) formats. At export
  // time the deferred value has converged, so the crop width is unchanged.
  const captureWidthClass = PLATFORM_META[deferredConfig.platform].captureWidthClass

  // Memoize the preview ELEMENT so the per-keystroke urgent re-render (where
  // `deferredConfig` is still the previous, referentially-stable value) reuses
  // it and React BAILS OUT of re-rendering the heavy 7-platform <SocialPreview>.
  // Without this bail-out `useDeferredValue` is a net loss — the preview
  // re-renders on the urgent pass (old config) AND again on the deferred pass,
  // double work that *raised* INP in benchmarking. `handleInteraction` is a
  // stable useCallback, so the only changing dep is `deferredConfig`.
  const socialPreview = useMemo(
    () => <SocialPreview config={deferredConfig} onInteraction={handleInteraction} />,
    [deferredConfig, handleInteraction],
  )

  return (
    <div className="flex w-full flex-col gap-6 lg:flex-row">
      <aside className="w-full space-y-6 rounded-3xl border border-slate-200 bg-white/80 p-4 shadow-lg backdrop-blur lg:w-[340px] dark:border-slate-800 dark:bg-slate-900/60">
        {/* Platform */}
        <section aria-labelledby="social-platform">
          <div className="flex items-center justify-between mb-1">
            <h2 id="social-platform" className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Platform
            </h2>
          </div>
          <div className="grid grid-cols-2 gap-2 text-xs">
            {(Object.keys(platformLabels) as SocialPlatform[]).map(platform => (
              <button
                key={platform}
                type="button"
                onClick={() => setConfig(prev => ({ ...prev, platform }))}
                className={clsx(
                  'flex items-center justify-center gap-1 rounded-lg border px-2 py-1 font-medium transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900 dark:focus-visible:outline-sky-400',
                  config.platform === platform
                    ? 'border-transparent bg-slate-900 text-white shadow-md dark:bg-sky-500'
                    : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 dark:border-slate-700 dark:bg-slate-900 dark:hover:border-slate-600'
                )}
                aria-pressed={config.platform === platform}
              >
                <span>{platformLabels[platform]}</span>
              </button>
            ))}
          </div>
        </section>

        {/* Branding & identity */}
        <section aria-labelledby="branding" className="space-y-2">
          <h3 id="branding" className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Branding & Identity
          </h3>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <label className="flex flex-col gap-0.5">
              <span className="font-medium text-slate-700 dark:text-slate-200">Name</span>
              <input
                type="text"
                value={config.username}
                onChange={event => setConfig(prev => ({ ...prev, username: event.target.value }))}
                className="rounded border border-slate-300 bg-white px-2 py-1 text-xs text-slate-900 shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
              />
            </label>
            <label className="flex flex-col gap-0.5">
              <span className="font-medium text-slate-700 dark:text-slate-200">Handle</span>
              <input
                type="text"
                value={config.handle}
                onChange={event => setConfig(prev => ({ ...prev, handle: event.target.value }))}
                className="rounded border border-slate-300 bg-white px-2 py-1 text-xs text-slate-900 shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
              />
            </label>
            <label className="flex flex-col gap-0.5 col-span-2">
              <span className="font-medium text-slate-700 dark:text-slate-200">Subtitle</span>
              <input
                type="text"
                value={config.subtitle}
                onChange={event => setConfig(prev => ({ ...prev, subtitle: event.target.value }))}
                className="rounded border border-slate-300 bg-white px-2 py-1 text-xs text-slate-900 shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
              />
            </label>
            {/* Headline is the video title — only the YouTube card renders it. */}
            {config.platform === 'youtube' && (
              <label className="flex flex-col gap-0.5 col-span-2">
                <span className="font-medium text-slate-700 dark:text-slate-200">Video title</span>
                <input
                  type="text"
                  value={config.headline}
                  onChange={event => setConfig(prev => ({ ...prev, headline: event.target.value }))}
                  className="rounded border border-slate-300 bg-white px-2 py-1 text-xs text-slate-900 shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                />
              </label>
            )}
            <label className="flex flex-col gap-0.5 col-span-2">
              <span className="font-medium text-slate-700 dark:text-slate-200">Post copy</span>
              <textarea
                value={config.content}
                onChange={event => setConfig(prev => ({ ...prev, content: event.target.value }))}
                className="min-h-[80px] rounded border border-slate-300 bg-white px-2 py-1 text-xs leading-relaxed text-slate-900 shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
              />
            </label>
            <label className="flex flex-col gap-0.5 col-span-2">
              <span className="font-medium text-slate-700 dark:text-slate-200">Hashtags</span>
              <div className="flex items-center gap-1 rounded border border-slate-300 bg-white px-2 py-1 shadow-sm focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-slate-900 dark:border-slate-700 dark:bg-slate-900">
                <Hash className="h-3 w-3 text-slate-400" />
                <input
                  type="text"
                  value={config.hashtags}
                  onChange={event => setConfig(prev => ({ ...prev, hashtags: event.target.value }))}
                  className="flex-1 border-none bg-transparent text-xs text-slate-900 placeholder:text-slate-400 focus:outline-none dark:text-white"
                />
              </div>
            </label>

            {/* Sound caption — TikTok / Reels only */}
            {PLATFORM_META[config.platform].vertical && (
              <label className="flex flex-col gap-0.5 col-span-2">
                <span className="font-medium text-slate-700 dark:text-slate-200">Sound / audio</span>
                <div className="flex items-center gap-1 rounded border border-slate-300 bg-white px-2 py-1 shadow-sm focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-slate-900 dark:border-slate-700 dark:bg-slate-900">
                  <Music className="h-3 w-3 text-slate-400" />
                  <input
                    type="text"
                    value={config.soundName ?? ''}
                    placeholder="original sound - adsights"
                    onChange={event => setConfig(prev => ({ ...prev, soundName: event.target.value }))}
                    className="flex-1 border-none bg-transparent text-xs text-slate-900 placeholder:text-slate-400 focus:outline-none dark:text-white"
                  />
                </div>
              </label>
            )}

            {/* Duration badge — YouTube only */}
            {config.platform === 'youtube' && (
              <label className="flex flex-col gap-0.5 col-span-2">
                <span className="font-medium text-slate-700 dark:text-slate-200">Video duration</span>
                <input
                  type="text"
                  value={config.duration ?? ''}
                  placeholder="12:34"
                  onChange={event => setConfig(prev => ({ ...prev, duration: event.target.value }))}
                  className="rounded border border-slate-300 bg-white px-2 py-1 text-xs text-slate-900 shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                />
              </label>
            )}
          </div>
        </section>

        {/* Visuals */}
        <section aria-labelledby="visuals" className="space-y-2">
          <h3 id="visuals" className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Visuals
          </h3>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="flex items-center justify-center gap-1 rounded-lg border border-dashed border-slate-300 bg-white py-1.5 text-xs font-medium text-slate-700 transition hover:border-slate-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 col-span-2"
            >
              <Upload className="h-3 w-3" />
              Upload media
            </button>
            <input
              type="file"
              accept="image/*"
              ref={fileInputRef}
              onChange={handleImageUpload}
              className="hidden"
            />
            <label className="flex flex-col gap-0.5 col-span-2">
              <span className="font-medium text-slate-700 dark:text-slate-200">Background</span>
              <input
                type="text"
                value={config.background}
                onChange={event => setConfig(prev => ({ ...prev, background: event.target.value }))}
                className="rounded border border-slate-300 bg-white px-2 py-1 text-xs text-slate-900 shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
              />
              <div className="mt-1 grid grid-cols-3 gap-1">
                {gradientPresets.map(gradient => (
                  <button
                    key={gradient}
                    type="button"
                    onClick={() => setConfig(prev => ({ ...prev, background: gradient }))}
                    className="h-7 rounded-lg border border-transparent focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900"
                    style={{ background: gradient }}
                  >
                    <span className="sr-only">Use gradient preset</span>
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={randomizeGradient}
                className="mt-2 inline-flex items-center justify-center gap-1 rounded bg-slate-900 px-2 py-2 text-xs font-medium text-white shadow-sm transition hover:bg-slate-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900 dark:bg-sky-500 dark:hover:bg-sky-400"
              >
                <RefreshCw className="h-3 w-3" />
                Shuffle
              </button>
            </label>
            <label className="flex flex-col gap-0.5 col-span-2 text-xs">
              <span className="font-xs text-slate-700 dark:text-slate-200">Avatar</span>
              <input
                type="file"
                accept="image/*"
                onChange={handleAvatarUpload}
                className="rounded border border-dashed border-slate-300 bg-white px-3 py-2 text-xs text-slate-900 shadow-sm file:mr-4 file:rounded-md file:border-0 file:bg-slate-900 file:px-3 file:py-2 file:text-xs file:font-medium file:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:file:bg-sky-500"
              />
            </label>
          </div>
        </section>

        {/* Engagement */}
        <section aria-labelledby="engagement" className="space-y-2">
          <h2 id="engagement" className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Engagement
          </h2>
          <div className="flex items-center justify-between text-xs gap-2">
            <label className="flex items-center gap-1 text-slate-700 dark:text-slate-200">
              <input
                type="checkbox"
                checked={config.showMetrics}
                onChange={event => setConfig(prev => ({ ...prev, showMetrics: event.target.checked }))}
                className="h-3 w-3 rounded border-slate-300 text-slate-900 focus:ring-slate-900 dark:border-slate-600 dark:text-sky-500 dark:focus:ring-sky-500"
              />
              Show engagement metrics
            </label>
            <label className="flex items-center gap-1 text-slate-700 dark:text-slate-200">
              <input
                type="checkbox"
                checked={config.verified}
                onChange={event => setConfig(prev => ({ ...prev, verified: event.target.checked }))}
                className="h-3 w-3 rounded border-slate-300 text-slate-900 focus:ring-slate-900 dark:border-slate-600 dark:text-sky-500 dark:focus:ring-sky-500"
              />
              Verified badge
            </label>
          </div>
          <div
            className={clsx(
              'grid gap-2 text-xs',
              visibleMetrics.length === 1 ? 'grid-cols-1' : visibleMetrics.length === 2 ? 'grid-cols-2' : 'grid-cols-3',
            )}
          >
            {visibleMetrics.map(key => (
              <label key={key} className="flex flex-col gap-0.5">
                <span className="font-medium text-slate-700 dark:text-slate-200">{metricLabels[key] ?? DEFAULT_METRIC_LABELS[key]}</span>
                <input
                  type="number"
                  min={0}
                  value={config.metrics[key]}
                  onChange={event =>
                    setConfig(prev => ({
                      ...prev,
                      metrics: {
                        ...prev.metrics,
                        [key]: Number.parseInt(event.target.value, 10) || 0,
                      },
                    }))
                  }
                  className="rounded border border-slate-300 bg-white px-2 py-1 text-xs text-slate-900 shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                />
              </label>
            ))}
          </div>
          <label className="flex flex-col gap-0.5 text-xs">
            <span className="font-medium text-slate-700 dark:text-slate-200">Timestamp</span>
            <input
              type="text"
              value={config.timestamp}
              onChange={event => setConfig(prev => ({ ...prev, timestamp: event.target.value }))}
              className="rounded border border-slate-300 bg-white px-2 py-1 text-xs text-slate-900 shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
            />
          </label>
          {isThemeAware && (
            <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-white px-2 py-2 text-xs shadow-sm dark:border-slate-700 dark:bg-slate-900 mt-1">
              <div className="flex items-center gap-2">
                <Palette className="h-3 w-3 text-slate-500" />
                <span className="font-medium text-slate-700 dark:text-slate-200">Theme</span>
              </div>
              <button
                type="button"
                onClick={() => setConfig(prev => ({ ...prev, theme: prev.theme === 'dark' ? 'light' : 'dark' }))}
                className="rounded bg-slate-900 px-2 py-1 text-xs font-medium text-white shadow-sm transition hover:bg-slate-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900 dark:bg-sky-500 dark:hover:bg-sky-400"
              >
                {themeLabel}
              </button>
            </div>
          )}
        </section>

        {/* Export */}
        <section aria-labelledby="export" className="space-y-2">
          <h3 id="export" className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Export
          </h3>
          <p className="text-sm text-slate-500 dark:text-slate-300">
            Download the mockup as a high-resolution PNG (captured at 2&times; retina density) — deck-ready, with no watermark.
          </p>
          <ExportButton
            isExporting={isExporting}
            onExport={() => void handleExport()}
            icon={<Download className="h-3 w-3" />}
            className="w-full gap-1 rounded-lg bg-slate-900 px-2 py-2 text-xs font-semibold text-white shadow-lg transition hover:bg-slate-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-sky-500 dark:hover:bg-sky-400"
          />
          {showExportHint && (
            <GeneratorExportBridge resourceSlug={trackingSlug} getEngagementMs={getTotalEngagementMs} />
          )}
        </section>
      </aside>

      <div
        className="relative flex flex-1 items-center justify-center rounded-3xl border border-white/10 bg-slate-950/80 p-6 shadow-2xl transition dark:border-slate-800"
        style={{ background: config.background }}
      >
        <div className="absolute inset-6 rounded-[40px] border border-white/10 opacity-50" aria-hidden="true" />
        {/*
          The capture wrapper is constrained to the active format's own
          card width (rather than the full panel width) so the exported
          PNG is cropped to the mockup itself. This matters most for the
          9:16 vertical formats (TikTok/Reels), whose ~320px card would
          otherwise export with large transparent side margins. We keep
          `w-full` + a definite `max-w-*` (not `w-fit`) so the card's own
          `w-full` child still resolves to a real width — a shrink-to-fit
          wrapper collapses to 0 and breaks the html2canvas capture.
        */}
        <div className={clsx('relative z-10 mx-auto w-full', captureWidthClass)} ref={previewRef}>
          {socialPreview}
        </div>
      </div>

      {showGrowthFunnel && (
        <PostExportPrompt
          open={postExportOpen}
          onOpenChange={onPostExportOpenChange}
          resourceSlug={trackingSlug}
          getEngagementMs={getTotalEngagementMs}
        />
      )}
    </div>
  )
}
